'use client';
import { Landscape_Description } from '@/components/ui/catalog/landscape/description';
import { Landscape_Display } from '../landscape/LandscapeDisplay';
import { Landscape_Immersive } from '../landscape/LandscapeImmersive';
import type {
  Collaboration,
  LandscapeDisplay,
  LandscapeImmersive,
  LandscapeDescription,
  LandscapeChapterHero,
  LandscapeChapter,
  LandscapeProcess,
  LandscapeSketchbook,
  LandscapeVisionDisplay,
  LandscapeDocumentary,
  LandscapeRenders,
  LandscapePress,
  LandscapeGallery,
  LandscapeExhibition,
  LandscapeEmbed,
} from '@/types';
import { Landscape_ChapterHero } from '../landscape/LandscapeChapterHero';
import { Landscape_Chapter } from '../landscape/LandscapeChapter';
import { Landscape_Process } from '../landscape/LandscapeProcess';
import { Landscape_Sketchbook } from '../landscape/LandscapeSketchbook';
import { Common_HeroSection } from '../landscape/CommonHeroSection';
import { EntryInformation, EntryPhotos, readLayout, type EntrySpec } from '../landscape/EntryInformation';
import { Landscape_Documentary } from '../landscape/LandscapeDocumentary';
import { Landscape_Renders } from '../landscape/LandscapeRender';
import { Landscape_Gallery } from '../landscape/LandscapeGallery';
import { Landscape_Press } from '../landscape/LandscapePress';
import { Landscape_Exhibition } from '../landscape/LandscapeExhibition';
import { Landscape_Embed } from '../landscape/LandscapeEmbed';

interface CollaborationProps {
  detail: Collaboration;
}

export default function CollaborationDetailPage({ detail }: CollaborationProps) {
  // Etiketleri dönüştür
  const tags: string[] = [];

  // Etiketleri ekle
  if (detail.tags && Array.isArray(detail.tags)) {
    detail.tags.forEach(tag => {
      if (typeof tag === 'number') {
        tags.push(String(tag));
      } else if (tag && typeof tag === 'object' && 'title' in tag) {
        tags.push(tag.title);
      }
    });
  }

  if (detail.date) {
    const date = new Date(detail.date);
    const year = date.getFullYear();
    if (year) tags.push(year.toString());
  } else {
    // Tarih yoksa createdAt kullan
    const year = new Date(detail.createdAt).getFullYear();
    if (year) tags.push(year.toString());
  }

  // What the entry knows about itself. Shown by the description block where the layout has
  // one, and by the page itself where it does not, so no entry is left as a bare title.
  const specs: EntrySpec[] = [];

  if (detail.type) {
    specs.push({
      label: 'Type',
      specifications: [{ value: detail.type }],
    });
  }
  // typeof detail.status
  if (detail.status && typeof detail.status === 'object') {
    specs.push({
      label: 'Status',
      specifications: [{ value: detail.status.title }],
    });
  }
  // date dd/mm/yyyy
  if (detail.date) {
    specs.push({
      label: 'Date',
      specifications: [{ value: new Date(detail.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) }],
    });
  }

  if (detail.highlights?.length) {
    specs.push({
      label: 'Highlights',
      specifications: detail.highlights.map(highlight => ({ value: highlight.highlight })),
    });
  }

  if (detail.projectUrl) {
    specs.push({
      label: 'Project URL',
      specifications: [
        {
          value: new URL(detail.projectUrl).hostname.replace(/^www\./, ''),
          url: detail.projectUrl,
          light: true,
        },
      ],
    });
  }
  // partners
  if (detail.partners?.length) {
    specs.push({
      label: 'Partners',
      specifications: detail.partners.map(partner => ({ value: `${partner.name} - ${partner.role}` })),
    });
  }
  const page = readLayout(detail.layout);

  const GetLayout = ({
    layout,
  }: {
    layout:
      | LandscapeDisplay
      | LandscapeImmersive
      | LandscapeVisionDisplay
      | LandscapeDescription
      | LandscapeChapterHero
      | LandscapeChapter
      | LandscapeProcess
      | LandscapeSketchbook
      | LandscapeDocumentary
      | LandscapeRenders
      | LandscapeGallery
      | LandscapePress
      | LandscapeExhibition
      | LandscapeEmbed;
  }) => {
    switch (layout.blockType) {
      case 'LandscapeDisplay':
        return <Landscape_Display layout={layout} />;
      case 'LandscapeImmersive':
        return <Landscape_Immersive layout={layout} />;
      case 'LandscapeChapterHero':
        return <Landscape_ChapterHero layout={layout} />;
      case 'LandscapeChapter':
        return <Landscape_Chapter layout={layout} />;
      case 'LandscapeProcess':
        return <Landscape_Process layout={layout} />;
      case 'LandscapeSketchbook':
        return <Landscape_Sketchbook layout={layout} />;
      case 'LandscapeDocumentary':
        return <Landscape_Documentary layout={layout} />;
      case 'LandscapeRenders':
        return <Landscape_Renders layout={layout} />;
      case 'LandscapePress':
        return <Landscape_Press layout={layout} />;
      case 'LandscapeGallery':
        return <Landscape_Gallery layout={layout} />;
      case 'LandscapeExhibition':
        return <Landscape_Exhibition layout={layout} />;
      case 'LandscapeEmbed':
        return <Landscape_Embed layout={layout} />;
      case 'LandscapeDescription':
        return <Landscape_Description layout={layout} specs={specs} />;
      default:
        return null; // Eşleşme yoksa null döndür
    }
  };

  // Content area - ensure overflow works with parent container
  return (
    <div
      className="w-full bg-black relative h-full overflow-y-auto"
      style={{ scrollbarWidth: 'none' }}
      id="landscape-content"
    >
      {/* Hero Section */}
      <Common_HeroSection
        title={detail.title}
        description={detail.description}
        leftTop={detail.subtitle}
        rightTop={detail.status && typeof detail.status === 'object' ? detail.status.title : ""}
        tags={tags}
      />

      {/* An entry with no description block still shows what it knows. */}
      {page.describes ? null : <EntryInformation specs={specs} />}

      {/* Installation Sections */}
      {detail.layout.map((lay, index) => (
        <GetLayout key={index} layout={lay} />
      ))}
      {/* The entry's own photographs, where the page shows no pictures or film of its own. */}
      {page.showsMedia ? null : <EntryPhotos photos={detail.photos} title={detail.title} />}
    </div>
  );
}
